/**
 * `/content/posts/new` — Create a post manually or via AI generation.
 *
 * Two modes toggled via tab:
 *   - "Write" — paste your own copy, pick platform, optional schedule.
 *   - "Generate" — pick platform + topic; AI returns a draft saved immediately.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ImageUploadField } from "@/components/image-upload-field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  useBrandVoices,
  useCreatePost,
  useGeneratePost,
  useUpdatePost,
} from "@/hooks/use-content";
import type { ContentPostResponse } from "@/lib/api-client";

const PLATFORMS: Array<{ value: ContentPostResponse["platform"]; label: string }> = [
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "twitter", label: "X / Twitter" },
  { value: "blog", label: "Blog" },
];

function PlatformSelect({
  value,
  onChange,
}: {
  value: ContentPostResponse["platform"];
  onChange: (v: ContentPostResponse["platform"]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {PLATFORMS.map((p) => (
        <button
          key={p.value}
          type="button"
          onClick={() => onChange(p.value)}
          className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
            value === p.value
              ? "bg-primary text-primary-foreground border-primary"
              : "border-gray-200 text-gray-700 hover:border-gray-400"
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

export function ContentPostNewPage() {
  const navigate = useNavigate();
  const voices = useBrandVoices();
  const createPost = useCreatePost();
  const generatePost = useGeneratePost();
  const updatePost = useUpdatePost();

  const [mode, setMode] = useState<"write" | "generate">("generate");
  const [platform, setPlatform] = useState<ContentPostResponse["platform"]>("instagram");
  const [body, setBody] = useState("");
  const [topic, setTopic] = useState("");
  const [additionalInstructions, setAdditionalInstructions] = useState("");
  const [brandVoiceId, setBrandVoiceId] = useState<string>("default");
  const [imageUrl, setImageUrl] = useState("");

  const imageRequired = platform === "instagram";
  const trimmedImageUrl = imageUrl.trim();
  const mediaUrls = trimmedImageUrl ? [trimmedImageUrl] : [];

  function handleWrite(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    createPost.mutate(
      { platform, body: body.trim(), media_urls: mediaUrls },
      { onSuccess: (post) => navigate(`/content/posts/${post.id}`) },
    );
  }

  function handleGenerate(e: React.FormEvent) {
    e.preventDefault();
    if (!topic.trim()) return;
    generatePost.mutate(
      {
        platform,
        topic: topic.trim(),
        ...(brandVoiceId !== "default" && { brand_voice_id: brandVoiceId }),
        ...(additionalInstructions && { additional_instructions: additionalInstructions }),
      },
      {
        onSuccess: (post) => {
          // Generate API doesn't accept media_urls; attach via follow-up update
          // so generated posts can carry an image URL too.
          if (mediaUrls.length > 0) {
            updatePost.mutate(
              { id: post.id, body: { media_urls: mediaUrls } },
              { onSettled: () => navigate(`/content/posts/${post.id}`) },
            );
          } else {
            navigate(`/content/posts/${post.id}`);
          }
        },
      },
    );
  }

  const isPending = createPost.isPending || generatePost.isPending;
  const error = createPost.error ?? generatePost.error;

  return (
    <div className="flex flex-col gap-6 max-w-lg">
      <header>
        <button
          type="button"
          onClick={() => navigate("/content")}
          className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1 hover:underline"
        >
          ← Content
        </button>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">New post</h1>
      </header>

      <Tabs value={mode} onValueChange={(v) => setMode(v as "write" | "generate")}>
        <TabsList>
          <TabsTrigger value="generate">AI Generate</TabsTrigger>
          <TabsTrigger value="write">Write manually</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="flex flex-col gap-1.5">
        <Label>Platform</Label>
        <PlatformSelect value={platform} onChange={setPlatform} />
      </div>

      <ImageUploadField
        value={imageUrl}
        onChange={setImageUrl}
        label="Image"
        required={imageRequired}
      />

      {mode === "generate" ? (
        <form onSubmit={handleGenerate} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="topic">Topic <span className="text-red-500">*</span></Label>
            <Input
              id="topic"
              placeholder="New product launch, tips for entrepreneurs…"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            />
          </div>

          {voices.data && voices.data.items.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="voice">Brand voice</Label>
              <select
                id="voice"
                value={brandVoiceId}
                onChange={(e) => setBrandVoiceId(e.target.value)}
                className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <option value="default">Default voice</option>
                {voices.data.items.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name} {v.is_default ? "(default)" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="extra">Additional instructions</Label>
            <Textarea
              id="extra"
              placeholder="Mention our spring sale, include a CTA…"
              value={additionalInstructions}
              onChange={(e) => setAdditionalInstructions(e.target.value)}
              rows={2}
              className="resize-none"
            />
          </div>

          {error && (
            <p className="text-sm text-destructive">
              {error instanceof Error ? error.message : "Generation failed."}
            </p>
          )}

          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={!topic.trim() || isPending || (imageRequired && !trimmedImageUrl)}
            >
              {isPending ? "Generating…" : "Generate post"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => navigate("/content")}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleWrite} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="body">Post copy <span className="text-red-500">*</span></Label>
            <Textarea
              id="body"
              placeholder="Write your post here…"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
            />
          </div>

          {error && (
            <p className="text-sm text-destructive">
              {error instanceof Error ? error.message : "Failed to create post."}
            </p>
          )}

          <div className="flex gap-3">
            <Button
              type="submit"
              disabled={!body.trim() || isPending || (imageRequired && !trimmedImageUrl)}
            >
              {isPending ? "Creating…" : "Save draft"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => navigate("/content")}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

export default ContentPostNewPage;
